import { useContext, useEffect, useMemo, useState } from 'react'
import { useNotifications } from 'shared-components/hooks/use-notifications'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  BADGE_LOOKS,
  communityHref,
  foundingMemberNo,
  knownTier,
  memberHubHref,
  memberSettingsHref,
  monthYear,
  ownStanding,
  tierLabel,
  tierProgress,
  type MemberHubPage,
} from 'lib/community/model'
import {
  activeHubTab,
  heroFacts,
  hubTabCount,
  hubTabs,
  ownerTabsNote,
  toNextTierLine,
} from 'lib/community/hub'
import type { PointsGuide } from 'lib/community/gamification'
import type { CommunityMemberResponse } from 'lib/community/types'
import Crumbs, { type Crumb } from './Crumbs'
import { HowPointsWork } from './PointsHelp'
import MemberName from './MemberName'
import TierAvatar from './TierAvatar'
import { HubNotificationsContext } from './HubNotificationsContext'
import { MemberViewContext, useOwnMemberMe } from './useOwnMemberMe'
import VerifiedCheck from './VerifiedCheck'
import base from './community.module.css'
import memberStyles from './Member.module.css'
import styles from './MemberHub.module.css'

/**
 * The member hub shell (Member Hub design 1a / 1b): every page under
 * `/community/members/<handle>` — the Overview, its tabs, the Badges page — renders inside it.
 *
 * The server HTML is the VISITOR page, the same for every reader (shared-cached, Constraint
 * 70f5a56d): handle, tier, public counts, the public tabs. The member's own layer — the hero
 * actions, owner-only tabs, Up next, the own badge view — hydrates through `useOwnMemberMe`
 * (897af2f1). "View as visitor" is client state in `MemberViewContext`: while on, every owner
 * layer reads null and the page is exactly the visitor render, with one "Back to your view" bar.
 */

export interface HubHeaderProps {
  member: CommunityMemberResponse
  trailingSlash: boolean
}

/** Share the page: the system sheet where there is one, else copy the address and say so. */
const ShareButton: React.VFC<{ path: string }> = ({ path }) => {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) {
      return undefined
    }
    const timer = window.setTimeout(() => setCopied(false), 2500)
    return () => window.clearTimeout(timer)
  }, [copied])
  const share = async (): Promise<void> => {
    const url = new URL(path, window.location.origin).toString()
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url })
      } catch {
        // Dismissed the sheet: nothing to say.
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      // No clipboard (an old browser, a denied permission): the address bar still has it.
    }
  }
  return (
    <span className={styles.share}>
      <button type="button" className={styles.ghost} onClick={() => void share()}>
        {'Share'}
      </button>
      {/* Announced politely; empty until a copy happened, and out of the row's flow. */}
      <span className={styles.copied} role="status">
        {copied ? 'Link copied' : ''}
      </span>
    </span>
  )
}

/** The hero's owner-only right column: "View as visitor", "Share", "Public · change". */
export const HubOwnerActionsView: React.VFC<{
  handle: string
  /** `/me` `community.profile_public`; undefined = DealHunt did not say (no status line). */
  isPublic: boolean | undefined
  trailingSlash: boolean
  onViewAsVisitor: () => void
}> = ({ handle, isPublic, trailingSlash, onViewAsVisitor }) => (
  <div className={styles.actions}>
    <div className={styles.actionRow}>
      <button type="button" className={styles.ghost} onClick={onViewAsVisitor}>
        {'View as visitor'}
      </button>
      <ShareButton path={memberHubHref(handle, trailingSlash)} />
    </div>
    {typeof isPublic === 'boolean' && (
      <span className={styles.status}>
        <span className={clsx(styles.dot, isPublic && styles.dotOn)} aria-hidden="true" />
        {isPublic ? 'Public · ' : 'Private · '}
        {/* The member's own Settings tab, where the switch lives (Member Hub phase 5a). */}
        <Linked href={memberSettingsHref(handle, trailingSlash)} className={base.cardLink}>
          {'change'}
        </Linked>
      </span>
    )}
  </div>
)

/**
 * The Overview's hero (1a): tier-ring avatar, handle with the signed-in check, tier chip and the
 * founding pills, "Joined … · N owner reviews · N Helpful", the points bar and "X to <tier>"
 * (no line at the top tier). `owner` is the hydrated owner column, null in the HTML.
 */
export const HubHeroView: React.VFC<
  HubHeaderProps & { guide: PointsGuide | null; owner: React.ReactNode }
> = ({ member, guide, owner }) => {
  const tier = knownTier(member.tier)
  const toNext = toNextTierLine(member.next_tier)
  const facts = heroFacts(member, monthYear(member.joined_at))
  const points = `${member.points.toLocaleString('en-US')} pts`
  return (
    <header className={styles.hero}>
      <TierAvatar
        handle={member.handle}
        tier={tier}
        size="xl"
        className={styles.heroAvatar}
        src={member.avatar_url}
        name={member.display_name}
      />
      <div className={styles.heroText}>
        <div className={styles.titleRow}>
          <h1 className={styles.handle}>
            <MemberName name={member.display_name} handle={member.handle} />
            <VerifiedCheck size="md" />
          </h1>
          <span className={clsx(styles.tierChip, base[`ring_${tier}`])}>{tierLabel(tier)}</span>
          {member.founding_number !== null && (
            <span className={base.founding}>
              {`${BADGE_LOOKS.founding_owner.name} #${member.founding_number}`}
            </span>
          )}
          {typeof member.founding_member_number === 'number' && (
            <span className={clsx(base.founding, memberStyles.memberPill)}>
              {`${BADGE_LOOKS.founding_member.name} `}
              <span className={memberStyles.memberNo}>
                {foundingMemberNo(member.founding_member_number)}
              </span>
            </span>
          )}
        </div>
        {facts.length > 0 && <p className={styles.facts}>{facts.join(' · ')}</p>}
        <div className={styles.points}>
          <span className={styles.pointsFigure}>
            <b>{points}</b>
            {guide && <HowPointsWork guide={guide} />}
          </span>
          <span className={styles.bar} aria-hidden="true">
            <span
              className={styles.barFill}
              style={{
                width: `${tierProgress(member.points, member.next_tier?.points_to_go ?? null)}%`,
              }}
            />
          </span>
          {toNext && <span className={styles.toNext}>{toNext}</span>}
        </div>
      </div>
      {owner}
    </header>
  )
}

/** The compact header (1b) on every hub page but the Overview: one row — avatar, handle, tier
 *  chip, points. `as` keeps one h1 per page (the Badges page has its own title). */
export const HubCompactHeader: React.VFC<HubHeaderProps & { as?: 'h1' | 'p' }> = ({
  member,
  as: Title = 'h1',
}) => {
  const tier = knownTier(member.tier)
  return (
    <header className={styles.compact}>
      <TierAvatar
        handle={member.handle}
        tier={tier}
        size="md"
        className={styles.compactAvatar}
        src={member.avatar_url}
        name={member.display_name}
      />
      <Title className={styles.compactHandle}>
        <MemberName name={member.display_name} handle={member.handle} />
      </Title>
      <span className={clsx(styles.tierChip, base[`ring_${tier}`])}>{tierLabel(tier)}</span>
      <span className={styles.compactPoints}>{`${member.points.toLocaleString('en-US')} pts`}</span>
    </header>
  )
}

/**
 * The tab bar: `HUB_TABS` in order, owner-only tabs only for the member themself (absent for a
 * visitor, never disabled), the "only visible to you" note only beside one. The Badges page marks
 * Overview (`activeHubTab`) as `aria-current="true"` — it sits under it; its own tab is the page.
 */
export const HubTabsView: React.VFC<
  HubHeaderProps & {
    page: MemberHubPage
    own: boolean
    /** The member's unread notifications (the bell's badge), owner view only. */
    unread?: { count: number; capped: boolean } | null
  }
> = ({ member, page, own, trailingSlash, unread = null }) => {
  const shown = hubTabs(own)
  const note = ownerTabsNote(shown)
  const active = activeHubTab(page)
  return (
    <nav className={styles.tabs} aria-label={`${member.handle}’s pages`}>
      {shown.map(tab => {
        const on = tab.id === active
        const count = hubTabCount(tab, member, unread)
        let current: 'page' | 'true' | undefined
        if (on) {
          current = tab.id === page ? 'page' : 'true'
        }
        return (
          <Linked
            key={tab.id}
            href={memberHubHref(member.handle, trailingSlash, tab.id)}
            className={clsx(styles.tab, on && styles.tabOn)}
            aria-current={current}
          >
            {tab.label}
            {count !== null && (
              <span className={styles.tabCount}>
                {typeof count === 'number' ? count.toLocaleString('en-US') : count}
              </span>
            )}
          </Linked>
        )
      })}
      {note && <span className={styles.tabNote}>{note}</span>}
    </nav>
  )
}

/** "You're seeing your page as visitors do." — the one control back, while the preview is on. */
export const VisitorPreviewBar: React.VFC<{ onBack: () => void }> = ({ onBack }) => (
  <div className={styles.preview} role="status">
    <span>{'You’re seeing your page as visitors do.'}</span>
    <button type="button" className={styles.ghost} onClick={onBack}>
      {'Back to your view'}
    </button>
  </div>
)

interface MemberHubProps {
  member: CommunityMemberResponse
  page: MemberHubPage
  homeHref: string
  /** The summary's points table (Overview hero's "How points work"); null = no button. */
  guide?: PointsGuide | null
  /** The Badges page's own h1 follows the header; elsewhere the handle is the h1. */
  children: React.ReactNode
}

const HubFrame: React.VFC<MemberHubProps> = ({
  member,
  page,
  homeHref,
  guide = null,
  children,
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const view = useContext(MemberViewContext)
  // `me`: the owner layers' view (null for a visitor AND while "View as visitor" is on);
  // `realMe`: whether the reader IS the member, for the one control that ends the preview.
  const me = useOwnMemberMe(member.handle)
  const realMe = useOwnMemberMe(member.handle, { evenAsVisitor: true })
  const standing = ownStanding(me, member.handle)
  // The member's own notifications (commentHub's bell feed): one instance per hub page, shared
  // with the Notifications tab's body by context. Inert unless the reader IS the member (and not
  // previewing as a visitor) — a visitor's hub sends no request. The header polls the badge;
  // this reads it once (no second 60 s poller).
  const notifications = useNotifications({ pollBadge: false, enabled: me !== null })
  const crumbs: Crumb[] = [
    { label: 'Home', href: homeHref },
    { label: 'Community', href: communityHref(trailingSlash) },
    // The member's page is out of the Leaderboard trail (Member Hub 1, reverses 398049d8).
    page === 'badges'
      ? { label: member.handle, href: memberHubHref(member.handle, trailingSlash) }
      : { label: member.handle },
  ]
  if (page === 'badges') {
    crumbs.push({ label: 'Badges' })
  }
  return (
    <>
      <Crumbs items={crumbs} />
      {view.asVisitor && realMe !== null && (
        <VisitorPreviewBar onBack={() => view.setAsVisitor(false)} />
      )}
      {page === 'overview' ? (
        <HubHeroView
          member={member}
          trailingSlash={trailingSlash}
          guide={guide}
          owner={
            me !== null ? (
              <HubOwnerActionsView
                handle={member.handle}
                isPublic={standing?.profile_public}
                trailingSlash={trailingSlash}
                onViewAsVisitor={() => view.setAsVisitor(true)}
              />
            ) : null
          }
        />
      ) : (
        <HubCompactHeader
          member={member}
          trailingSlash={trailingSlash}
          as={page === 'badges' ? 'p' : 'h1'}
        />
      )}
      <HubNotificationsContext.Provider value={me !== null ? notifications : null}>
        <HubTabsView
          member={member}
          page={page}
          own={me !== null}
          trailingSlash={trailingSlash}
          unread={
            me !== null ? { count: notifications.unread, capped: notifications.capped } : null
          }
        />
        {children}
      </HubNotificationsContext.Provider>
    </>
  )
}

const previewKey = (handle: string): string => `sg:member-hub-visitor-view:${handle.toLowerCase()}`

/** "View as visitor" for this handle in this browser tab — sessionStorage, never the URL (a
 *  `?view=` would be one more edge-cache key on a shared-cached page). Storage can throw
 *  (private mode, blocked site data): then the preview just does not persist. */
function readVisitorPreview(handle: string): boolean {
  try {
    return window.sessionStorage.getItem(previewKey(handle)) === '1'
  } catch {
    return false
  }
}

function writeVisitorPreview(handle: string, on: boolean): void {
  try {
    if (on) {
      window.sessionStorage.setItem(previewKey(handle), '1')
    } else {
      window.sessionStorage.removeItem(previewKey(handle))
    }
  } catch {
    // Not persisted; the toggle still works on this page.
  }
}

/** The shell with its "View as visitor" state — one per hub page. */
export const MemberHub: React.VFC<MemberHubProps> = props => {
  const { handle } = props.member
  const [asVisitor, setAsVisitorState] = useState(false)
  // The preview follows the member across the hub's tabs for the browser tab's session: each hub
  // page mounts its own shell. Read after mount (the first render is always the server's).
  useEffect(() => {
    setAsVisitorState(readVisitorPreview(handle))
  }, [handle])
  const view = useMemo(
    () => ({
      asVisitor,
      setAsVisitor: (on: boolean) => {
        setAsVisitorState(on)
        writeVisitorPreview(handle, on)
      },
    }),
    [asVisitor, handle]
  )
  return (
    <MemberViewContext.Provider value={view}>
      <HubFrame {...props} />
    </MemberViewContext.Provider>
  )
}
